import type { Instance, SnapshotIn, SnapshotOut } from "mobx-state-tree";
import { flow, types } from "mobx-state-tree";
import * as orgRepo from "./organizations/repository";
import { track } from "@/lib/posthog";
import type { Database } from "@/integrations/supabase/types";
import type { RealtimeChannel } from "@supabase/supabase-js";
import type {
  MemberProjectAccessRow,
  PermissionCatalog,
  ProjectPermissionsPayload,
} from "@/lib/memberProjectPermissions";

const OrgUser = types.model("OrgUser", {
  id: types.number,
  first_name: types.maybeNull(types.string),
  last_name: types.maybeNull(types.string),
  email: types.string,
  avatar_url: types.maybeNull(types.string),
  color: types.maybeNull(types.string),
  org_id: types.string,
  role: types.string,
  status: types.optional(types.string, "active"),
});

const UserInvitation = types.model("UserInvitation", {
  id: types.number,
  invite_token: types.string,
  invitee_email: types.string,
  invited_role: types.string,
  status: types.string,
  expires_at: types.string,
  created_at: types.string,
  org_id: types.string,
  organisation_name: types.maybeNull(types.string),
  organisation_domain: types.maybeNull(types.string),
});

export const OrganizationStore = types
  .model("OrganizationStore", {
    users: types.array(OrgUser),
    invitations: types.array(UserInvitation),
    isLoading: types.optional(types.boolean, false),
    isLoadingInvitations: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
    // Invitations get their own error slot rather than sharing `error`: the
    // list is refetched on tab return, and a failed background refresh writing
    // `error` would replace whatever a settings form last put there
    // (UserRolesSection renders that slot verbatim).
    invitationsError: types.maybeNull(types.string),
    // Map of user_id to array of building_ids
    userBuildingAssignments: types.optional(types.map(types.array(types.number)), {}),
  })
  .volatile(() => ({
    channel: null as RealtimeChannel | null,
    // Which address `invitations` describes, and which fetch may still write to
    // it. Volatile because both are about the requests in flight right now, not
    // about the data the store holds.
    invitationsEmail: null as string | null,
    invitationsRequestId: 0,
  }))
  .actions((self) => ({
    updateOrganization: flow(function* updateOrganization(
      orgId: string,
      updates: {
        name?: string;
        domain?: string | null;
        settings?: Record<string, unknown>;
        logo_url?: string | null;
      }
    ) {
      self.error = null;
      try {
        yield orgRepo.updateOrganization(orgId, updates);
        return true;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        return false;
      }
    }),

    fetchOrganizationUsers: flow(function* fetchOrganizationUsers(orgId: string) {
      self.isLoading = true;
      self.error = null;
      try {
        const rolesData = yield orgRepo.fetchOrgUserRoles(orgId);
        const userIds = rolesData.map((r: { user_id: number }) => r.user_id);

        if (userIds.length === 0) {
          self.users.replace([]);
          return;
        }

        const profilesData = yield orgRepo.fetchUserProfiles(userIds);

        type RoleRow = Database["public"]["Tables"]["org_user_roles"]["Row"];
        type ProfileRow = {
          id: number;
          first_name: string | null;
          last_name: string | null;
          email: string;
          avatar_url: string | null;
          color: string | null;
        };

        const mapped = (rolesData as RoleRow[]).map((role) => {
          const profile = (profilesData as ProfileRow[]).find((p) => p.id === role.user_id);
          return {
            id: role.user_id,
            first_name: profile?.first_name || null,
            last_name: profile?.last_name || null,
            email: profile?.email || "",
            avatar_url: profile?.avatar_url ?? null,
            color: profile?.color ?? null,
            org_id: role.org_id,
            role: role.role,
            status: role.status || "active",
          };
        });

        self.users.replace(mapped);
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
      } finally {
        self.isLoading = false;
      }
    }),

    fetchUserBuildingAssignments: flow(function* fetchUserBuildingAssignments(orgId: string) {
      self.error = null;
      try {
        const assignments = yield orgRepo.fetchUserBuildingAssignments(orgId);

        // Clear existing assignments
        self.userBuildingAssignments.clear();

        // Add new assignments
        Object.entries(assignments as Record<number, number[]>).forEach(([userId, buildingIds]) => {
          self.userBuildingAssignments.set(userId, buildingIds);
        });
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
      }
    }),

    updateUserRole: flow(function* updateUserRole(
      orgId: string,
      userId: number,
      currentRole: string,
      updates: { role?: string; status?: string },
      modifiedBy: number
    ) {
      self.error = null;
      try {
        yield orgRepo.updateUserRole(orgId, userId, currentRole, updates, modifiedBy);
        return true;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        return false;
      }
    }),

    setMemberRole: flow(function* setMemberRole(orgId: string, userId: number, newRole: string) {
      self.error = null;
      try {
        yield orgRepo.setMemberRole(orgId, userId, newRole);
        return { success: true as const };
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        return { success: false as const, error: self.error };
      }
    }),

    removeMember: flow(function* removeMember(orgId: string, userId: number) {
      self.error = null;
      try {
        yield orgRepo.removeMember(orgId, userId);
        return { success: true as const };
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        return { success: false as const, error: self.error };
      }
    }),

    // These rethrow instead of falling back to an empty array: callers use the
    // project and folder lists to decide what a member may keep, so "the load
    // failed" must not be indistinguishable from "there are no projects" or
    // "this member is excluded from nothing" — the dialogs would render the
    // empty-state copy over a grant they cannot see and diff the next save
    // against it.
    fetchOrgProjects: flow(function* fetchOrgProjects(orgId: string) {
      try {
        const projects = yield orgRepo.fetchOrgProjects(orgId);
        return projects as orgRepo.OrgProjectOption[];
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    fetchOrgProjectFolders: flow(function* fetchOrgProjectFolders(orgId: string) {
      try {
        const folders = yield orgRepo.fetchOrgProjectFolders(orgId);
        return folders as orgRepo.OrgFolderOption[];
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    fetchProjectsByIds: flow(function* fetchProjectsByIds(orgId: string, projectIds: number[]) {
      try {
        const projects = yield orgRepo.fetchProjectsByIds(orgId, projectIds);
        return projects as orgRepo.ResolvedProjectOption[];
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    fetchSubcontractorProjectIds: flow(function* fetchSubcontractorProjectIds(
      orgId: string,
      userId: number
    ) {
      try {
        const ids = yield orgRepo.fetchSubcontractorProjectIds(orgId, userId);
        return ids as number[];
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    fetchSubcontractorExcludedFolderIds: flow(function* fetchSubcontractorExcludedFolderIds(
      orgId: string,
      userId: number
    ) {
      try {
        const ids = yield orgRepo.fetchSubcontractorExcludedFolderIds(orgId, userId);
        return ids as number[];
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    fetchFoldersInSubtrees: flow(function* fetchFoldersInSubtrees(
      rootFolderIds: number[],
      candidateFolderIds: number[]
    ) {
      try {
        const ids = yield orgRepo.fetchFoldersInSubtrees(rootFolderIds, candidateFolderIds);
        return ids as Set<number>;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    fetchProjectPermissionCatalog: flow(function* fetchProjectPermissionCatalog() {
      try {
        const catalog = yield orgRepo.fetchProjectPermissionCatalog();
        return catalog as PermissionCatalog | null;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    fetchMemberProjectAccess: flow(function* fetchMemberProjectAccess(
      orgId: string,
      userId: number
    ) {
      try {
        const rows = yield orgRepo.fetchMemberProjectAccess(orgId, userId);
        return rows as MemberProjectAccessRow[];
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    setSubcontractorProjects: flow(function* setSubcontractorProjects(
      orgId: string,
      userId: number,
      scope: orgRepo.ProjectScope,
      assignedBy: number,
      projectPermissions?: ProjectPermissionsPayload
    ) {
      self.error = null;
      try {
        yield orgRepo.setSubcontractorProjects(
          orgId,
          userId,
          scope,
          assignedBy,
          projectPermissions
        );
        return { success: true as const };
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        return { success: false as const, error: self.error };
      }
    }),

    subscribeToOrgChanges(orgId: string) {
      // Unsubscribe from any existing channel
      if (self.channel) {
        orgRepo.unsubscribeChannel(self.channel);
      }

      // Create new subscription using repository
      self.channel = orgRepo.subscribeToOrgUserRoles(orgId, () => {
        // Refetch users when changes occur
        this.fetchOrganizationUsers(orgId);
      });
    },

    unsubscribeFromOrgChanges() {
      if (self.channel) {
        orgRepo.unsubscribeChannel(self.channel);
        self.channel = null;
      }
    },

    /**
     * `background: true` for a refresh the user did not ask for — the focus
     * revalidation on the invitations page. It skips the loading flag, because
     * a spinner on every tab return would blank a list that is already correct,
     * and it never clears `invitations`: a transient failure must not render
     * the "no pending invitations" empty state, which is indistinguishable from
     * genuinely having none.
     */
    fetchUserInvitations: flow(function* fetchUserInvitations(
      email: string,
      opts: { background?: boolean } = {}
    ) {
      // Only the newest request may write. A background refresh started before
      // an accept or a decline routinely resolves after the refetch that
      // follows it, and replacing the list from it resurrects the invitation
      // the user just resolved, live Accept button and all.
      const requestId = ++self.invitationsRequestId;

      // The list belongs to ONE address, and this store outlives a sign-out —
      // nothing resets it on logout or on an account switch. Dropping it here
      // rather than on success is what stops the previous account's invitation
      // tokens from staying on screen, acceptable with one click, while the new
      // account's fetch is in flight or after it has failed.
      if (self.invitationsEmail !== email) {
        self.invitations.clear();
        self.invitationsEmail = email;
      }

      if (!opts.background) self.isLoadingInvitations = true;
      try {
        const data = yield orgRepo.fetchUserInvitations(email);
        if (requestId !== self.invitationsRequestId) return;

        const mapped = data.map((invite: orgRepo.UserInvitation) => ({
          id: invite.id,
          invite_token: invite.invite_token,
          invitee_email: invite.invitee_email,
          invited_role: invite.invited_role,
          status: invite.status,
          expires_at: invite.expires_at,
          created_at: invite.created_at,
          org_id: invite.org_id,
          organisation_name: invite.organisations?.name || null,
          organisation_domain: invite.organisations?.domain || null,
        }));

        self.invitations.replace(mapped);
        // Cleared on SUCCESS, never on entry: a background refresh clearing it
        // up front flashes off an error a foreground load just recorded, and
        // puts the "no pending invitations" empty state back over a failure for
        // as long as the refresh takes.
        self.invitationsError = null;
      } catch (e) {
        if (requestId !== self.invitationsRequestId) return;
        // Keep the last good list. Emptying it here is what turned a blip into
        // "you have no invitations".
        self.invitationsError = e instanceof Error ? e.message : String(e);
      } finally {
        // NOT generation-guarded: only a foreground call raises this flag, so a
        // superseded one skipping the clear would leave the spinner up for good.
        if (!opts.background) self.isLoadingInvitations = false;
      }
    }),

    acceptInvitation: flow(function* acceptInvitation(inviteToken: string, userId: string) {
      self.error = null;
      try {
        // Accept the invitation
        yield orgRepo.acceptInvitation(inviteToken, userId);

        // Refresh the auth session to update JWT claims
        yield orgRepo.refreshAuthSession();

        return { success: true };
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        return { success: false, error: self.error };
      }
    }),

    declineInvitation: flow(function* declineInvitation(inviteToken: string) {
      self.error = null;
      try {
        yield orgRepo.declineInvitation(inviteToken);
        return { success: true };
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        return { success: false, error: self.error };
      }
    }),

    createOrganization: flow(function* createOrganization(
      name: string,
      userId: number,
      // Analytics only: the nav menus are the settings surface, the auth-time
      // picker is onboarding. Defaulted so no caller has to care.
      source: "onboarding" | "settings" = "settings"
    ) {
      self.error = null;
      try {
        const result = yield orgRepo.createOrganization(name, userId);
        track("organization_created", { source });
        return { success: true, orgId: result.orgId };
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        return { success: false };
      }
    }),
  }));

export type OrganizationStoreInstance = Instance<typeof OrganizationStore>;
export type OrganizationStoreSnapshotIn = SnapshotIn<typeof OrganizationStore>;
export type OrganizationStoreSnapshotOut = SnapshotOut<typeof OrganizationStore>;
